// LLM generated - Model Claude Opus 4
/**
 * useContestChatState - Contest Chat State Orchestrator
 *
 * This hook orchestrates the chat state by composing:
 * - useContestChatUtils: Shared utilities for deduplication and caching
 * - useContestMessagesState: Messages (bidirectional) state and actions
 * - useContestPostsState: Posts (unidirectional) state and actions
 *
 * It also handles:
 * - Fetching chat channels
 * - Feature flags
 * - Pusher event subscriptions and routing
 * - Cleanup effects
 *
 * The exported API remains backwards compatible with the original hook.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { STALE_TIME } from '~/domains/contest/constants/query';
import { logError, logWarn } from '~/common/utils/log';
import { getChats } from '~/services/live-activity/live-activity-service.service.api';
import type {
  Chat,
  ChatMessage,
  ChatMessagesInfiniteData,
  GetUnreadMessagesResponse,
} from '~/services/live-activity/types';
import { useLiveActivityChat } from '~/services/live-activity/hooks/useLiveActivityChat';
import type {
  ChatMode,
  PusherMessageCreatedPayload,
  PusherMessageUpdatedPayload,
  PusherMessageDeletedPayload,
  PusherReactionAddedPayload,
  PusherReactionRemovedPayload,
  PusherChatStatePayload,
  PusherUserBannedPayload,
} from '~/services/live-activity/types/pusher-events';
import { useChatWebFeatureFlags } from '~/domains/common/hooks/useChatWebFeatureFlags';

import { createQueryKey } from '../constants/queryKeys';

import { useContestChatUtils } from './useContestChatUtils';
import { useContestMessagesState } from './useContestMessagesState';
import { useContestPostsState } from './useContestPostsState';

// Stable identity: `chats` is the main dep of the subscription effect, and a fresh []
// on every render while the query is in flight would retrigger it.
const EMPTY_CHATS: Chat[] = [];

// live-activity-service grants chat membership asynchronously after an entry (it consumes
// user-joined-contest off RabbitMQ) and sends the new entrant no Pusher event, so an empty
// list for a participant may only mean the membership has not landed yet. Poll briefly
// instead of holding that empty answer for the full STALE_TIME.
export const MEMBERSHIP_POLL_INTERVAL = 3_000;
export const MEMBERSHIP_POLL_WINDOW = 30_000;

// Outlives the hook: the provider remounts on every gamecenter page, and a per-mount window
// would restart the poll on each navigation for a participant whose contest has no chat.
const membershipPollStartedAt = new Map<string, number>();

export const resetMembershipPollWindows = () => membershipPollStartedAt.clear();

const getMembershipPollInterval = (pollKey: string): number | false => {
  const now = Date.now();
  const startedAt = membershipPollStartedAt.get(pollKey) ?? now;
  membershipPollStartedAt.set(pollKey, startedAt);

  return now - startedAt < MEMBERSHIP_POLL_WINDOW ? MEMBERSHIP_POLL_INTERVAL : false;
};

export type ChatViewer = 'participant' | 'spectator' | 'unknown';

export type UseContestChatStateProps = {
  contestId: string;
  contestName?: string;
  user: {
    id: string;
    handle?: string;
    avatarUrl?: string;
  } | null;
  isChatOpen?: boolean;
  activeTab: 'messages' | 'posts';
  viewer: ChatViewer;
};

export function useContestChatState({
  contestId,
  contestName,
  user,
  isChatOpen = true,
  activeTab,
  viewer,
}: UseContestChatStateProps) {
  const { t } = useTranslation('chat');

  const membershipPollKey = user ? `${user.id}:${contestId}` : undefined;

  useEffect(() => {
    if (viewer === 'spectator' && membershipPollKey) {
      membershipPollStartedAt.delete(membershipPollKey);
    }
  }, [viewer, membershipPollKey]);

  const [hasReceivedChatPusherMessage, setHasReceivedChatPusherMessage] = useState(false);
  const [hasReceivedPostPusherMessage, setHasReceivedPostPusherMessage] = useState(false);

  // Track if new messages arrived while viewing a different tab
  const hasUnreadSinceLastViewRef = useRef<{ messages: boolean; posts: boolean }>({
    messages: false,
    posts: false,
  });

  // --- Feature Flags ---

  const {
    isChatGloballyEnabled: finalIsChatGloballyEnabled,
    isChatWebEnabled: finalIsChatWebEnabled,
    isPostsWebEnabled: finalIsPostsWebEnabled,
  } = useChatWebFeatureFlags();

  // --- Fetch Chats ---

  const {
    data: chatsData,
    isLoading: isLoadingChats,
    error: chatsError,
  } = useQuery({
    queryKey: createQueryKey.liveActivityChats(contestId),
    // An empty list is a real answer, not a failure: /chats is scoped to the caller's chat
    // membership, so a non-entrant gets nothing back and no amount of retrying changes it.
    queryFn: () => getChats({ contestId, page: 1, pageSize: 20 }),
    enabled: finalIsChatGloballyEnabled && !!user && !!contestId,
    staleTime: STALE_TIME,
    refetchInterval: (data) =>
      viewer === 'participant' && data?.data.length === 0 && membershipPollKey
        ? getMembershipPollInterval(membershipPollKey)
        : false,
  });

  const chats = chatsData?.data ?? EMPTY_CHATS;
  const messagesChat = chats.find((chat) => chat.mode === 'bidirectional') || chats[0];
  const postsChat = chats.find((chat) => chat.mode === 'unidirectional') || chats[1];
  const chat = messagesChat || chats[0];
  const chatId = chat?.id;

  const chatEnabledFromAPI = chat?.isEnabled ?? true;
  const currentMuteNotifications = chat?.settings?.muteNotifications ?? false;
  const isChatEnabled = messagesChat?.isEnabled ?? true;
  const isPostsEnabled = postsChat?.isEnabled ?? true;

  // --- Shared Utilities ---

  const utils = useContestChatUtils({
    messagesChatId: messagesChat?.id,
    postsChatId: postsChat?.id,
  });

  const {
    queryClient,
    getQueryKeyForChatMode,
    messageExistsInCache,
    findMatchingPendingMessage,
    clearPendingMessage,
    checkAndClearPendingReaction,
    clearPendingMessagesForMode,
    clearStalePendingEntries,
    clearAllPendingEntries,
  } = utils;

  // --- Messages State ---

  // Messages enabled = feature flag enabled AND messages chat is enabled by commissioner
  const isMessagesChatEnabled = finalIsChatGloballyEnabled && isChatEnabled;

  const messagesState = useContestMessagesState({
    messagesChat,
    user,
    isChatOpen,
    isChatEnabled: isMessagesChatEnabled,
    utils,
  });

  // --- Posts State ---

  // Posts enabled = feature flag enabled AND posts chat is enabled by commissioner
  const isPostsChatEnabled = finalIsChatGloballyEnabled && isPostsEnabled;

  const postsState = useContestPostsState({
    postsChat,
    user,
    isChatOpen,
    isChatEnabled: isPostsChatEnabled,
    contestName,
    utils,
  });

  // --- Unified Reaction Handlers (routes to appropriate state hook) ---

  const handleAddReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (activeTab === 'posts') {
        await postsState.handleAddReaction(messageId, reactionCode);
      } else {
        await messagesState.handleAddReaction(messageId, reactionCode);
      }
    },
    [activeTab, messagesState, postsState]
  );

  const handleRemoveReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (activeTab === 'posts') {
        await postsState.handleRemoveReaction(messageId, reactionCode);
      } else {
        await messagesState.handleRemoveReaction(messageId, reactionCode);
      }
    },
    [activeTab, messagesState, postsState]
  );

  // --- Pusher Event Handlers ---

  const handleMessageCreated = useCallback(
    (chatMode: ChatMode, message: PusherMessageCreatedPayload) => {
      if (chatMode === 'bidirectional') {
        setHasReceivedChatPusherMessage(true);
      } else {
        setHasReceivedPostPusherMessage(true);
      }

      const isOwnMessage = message.user?.id === user?.id;

      // Check if this matches a pending optimistic message
      let matchedOptimisticId: string | null = null;
      if (isOwnMessage) {
        matchedOptimisticId = findMatchingPendingMessage(message.payload?.message || '', chatMode);
      }

      const queryKey = getQueryKeyForChatMode(chatMode);

      // Transform Pusher payload to ChatMessage format
      const chatMessage: ChatMessage = {
        id: message.id,
        publishedAt: message.publishedAt,
        payload: message.payload,
        user: message.user,
        reactions: message.reactions || [],
        userReactions: message.userReactions || [],
        updatedAt: message.updatedAt,
        isPinned: message.isPinned,
        splashMessage: message.splashMessage || false,
        mode: chatMode === 'bidirectional' ? 'bidirectional' : 'unidirectional',
      };

      if (matchedOptimisticId) {
        clearPendingMessage(matchedOptimisticId);

        if (chatMode === 'bidirectional') {
          messagesState.clearOptimisticMessages();
        } else {
          postsState.clearOptimisticPosts();
        }
      }

      // Add the real message to cache
      queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const allMessages = oldData.pages.flatMap((page) => page.data || []);
        if (allMessages.some((m) => m.id === chatMessage.id)) {
          return oldData;
        }

        const newPages = [...oldData.pages];
        if (newPages[0]) {
          newPages[0] = {
            ...newPages[0],
            data: [chatMessage, ...(newPages[0].data || [])],
          };
        }

        return { ...oldData, pages: newPages };
      });

      // Handle unread tracking and markAsRead
      const isViewingThisTab =
        isChatOpen &&
        ((chatMode === 'bidirectional' && activeTab === 'messages') ||
          (chatMode === 'unidirectional' && activeTab === 'posts'));

      if (isViewingThisTab) {
        if (chatMode === 'bidirectional') {
          messagesState.markAsReadMessages(chatMessage.id);
        } else {
          postsState.markAsReadPosts(chatMessage.id);
        }
      } else {
        if (chatMode === 'bidirectional') {
          hasUnreadSinceLastViewRef.current.messages = true;
        } else {
          hasUnreadSinceLastViewRef.current.posts = true;
        }

        // Optimistically increment unread count
        const unreadChatId = chatMode === 'bidirectional' ? messagesChat?.id : postsChat?.id;
        if (unreadChatId) {
          queryClient.setQueryData<GetUnreadMessagesResponse>(
            createQueryKey.unreadMessages(unreadChatId),
            (oldData) => {
              if (!oldData) {
                return { id: unreadChatId, data: { count: 1 } };
              }
              return {
                ...oldData,
                data: { count: (oldData.data?.count || 0) + 1 },
              };
            }
          );
        }
      }
    },
    [
      user?.id,
      messagesChat?.id,
      postsChat?.id,
      isChatOpen,
      activeTab,
      queryClient,
      getQueryKeyForChatMode,
      findMatchingPendingMessage,
      clearPendingMessage,
      messagesState,
      postsState,
    ]
  );

  const handleReactionAddedFromPusher = useCallback(
    (chatMode: ChatMode, payload: PusherReactionAddedPayload) => {
      const wasOurReaction = checkAndClearPendingReaction(payload.messageId, payload.code, 'add');
      const queryKey = getQueryKeyForChatMode(chatMode);

      if (!messageExistsInCache(payload.messageId, queryKey)) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }

      queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== payload.messageId) return msg;

            const reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === payload.code);

            if (wasOurReaction) {
              if (payload.newCount !== undefined && existingIdx >= 0) {
                reactions[existingIdx] = {
                  ...reactions[existingIdx],
                  count: payload.newCount,
                };
              }
            } else if (existingIdx >= 0) {
              reactions[existingIdx] = {
                ...reactions[existingIdx],
                count: payload.newCount ?? reactions[existingIdx].count + 1,
              };
            } else {
              reactions.push({ code: payload.code, count: payload.newCount ?? 1 });
            }

            return { ...msg, reactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });
    },
    [queryClient, getQueryKeyForChatMode, messageExistsInCache, checkAndClearPendingReaction]
  );

  const handleReactionRemovedFromPusher = useCallback(
    (chatMode: ChatMode, payload: PusherReactionRemovedPayload) => {
      const wasOurReaction = checkAndClearPendingReaction(
        payload.messageId,
        payload.code,
        'remove'
      );
      const queryKey = getQueryKeyForChatMode(chatMode);

      if (!messageExistsInCache(payload.messageId, queryKey)) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }

      queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== payload.messageId) return msg;

            let reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === payload.code);

            if (wasOurReaction) {
              if (payload.newCount !== undefined && existingIdx >= 0) {
                if (payload.newCount === 0) {
                  reactions = reactions.filter((r) => r.code !== payload.code);
                } else {
                  reactions[existingIdx] = {
                    ...reactions[existingIdx],
                    count: payload.newCount,
                  };
                }
              }
            } else if (existingIdx >= 0) {
              const newCount = payload.newCount ?? reactions[existingIdx].count - 1;
              if (newCount <= 0) {
                reactions = reactions.filter((r) => r.code !== payload.code);
              } else {
                reactions[existingIdx] = {
                  ...reactions[existingIdx],
                  count: newCount,
                };
              }
            }

            return { ...msg, reactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });
    },
    [queryClient, getQueryKeyForChatMode, messageExistsInCache, checkAndClearPendingReaction]
  );

  const handleMessageUpdatedFromPusher = useCallback(
    (chatMode: ChatMode, payload: PusherMessageUpdatedPayload) => {
      const queryKey = getQueryKeyForChatMode(chatMode);

      if (!messageExistsInCache(payload.id, queryKey)) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }

      queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id === payload.id) {
              return {
                ...msg,
                ...(payload.isPinned !== undefined && { isPinned: payload.isPinned }),
                ...(payload.payload && { payload: payload.payload }),
                updatedAt: payload.updatedAt,
              };
            }

            if (payload.isPinned === true && msg.isPinned) {
              return { ...msg, isPinned: false };
            }

            return msg;
          }),
        }));

        return { ...oldData, pages: newPages };
      });
    },
    [queryClient, getQueryKeyForChatMode, messageExistsInCache]
  );

  const handleMessageDeletedFromPusher = useCallback(
    (chatMode: ChatMode, payload: PusherMessageDeletedPayload) => {
      const queryKey = getQueryKeyForChatMode(chatMode);

      queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).filter((msg) => msg.id !== payload.id),
        }));

        return { ...oldData, pages: newPages };
      });
    },
    [queryClient, getQueryKeyForChatMode]
  );

  const handleChatStateChangedFromPusher = useCallback(
    (_chatMode: ChatMode, payload: PusherChatStatePayload) => {
      queryClient.invalidateQueries(createQueryKey.liveActivityChats(contestId));

      if (!payload.isEnabled) {
        showNotification({
          title: t('notifications.chatDisabled'),
          message: payload.reason || t('notifications.chatDisabledByCommissioner'),
          color: 'yellow',
        });
      }
    },
    [contestId, queryClient, t]
  );

  const handleUserBannedFromPusher = useCallback(
    (_chatMode: ChatMode, payload: PusherUserBannedPayload) => {
      if (payload.userId === user?.id) {
        showNotification({
          title: t('notifications.userBanned'),
          message: payload.reason || t('notifications.youHaveBeenBanned'),
          color: 'red',
        });

        queryClient.invalidateQueries(createQueryKey.liveActivityChats(contestId));
      }
    },
    [user?.id, contestId, queryClient, t]
  );

  const handleConnectionLost = useCallback(() => {
    logWarn('Pusher connection lost - data may be stale');
  }, []);

  const handleConnectionRestored = useCallback(() => {
    if (messagesChat?.id) {
      messagesState.refetchMessagesChat();
      messagesState.refetchUnreadMessages();
    }
    if (postsChat?.id) {
      postsState.refetchPostsChat();
      postsState.refetchUnreadPosts();
    }
  }, [messagesChat?.id, postsChat?.id, messagesState, postsState]);

  const handleEventTimeout = useCallback(
    (eventType: string, chatMode: ChatMode) => {
      logError(`Pusher event timeout: ${eventType} for ${chatMode}`);
      if (chatMode === 'bidirectional' && messagesChat?.id) {
        messagesState.refetchMessagesChat();
      } else if (chatMode === 'unidirectional' && postsChat?.id) {
        postsState.refetchPostsChat();
      }
    },
    [messagesChat?.id, postsChat?.id, messagesState, postsState]
  );

  // --- Pusher Subscription ---

  // Only subscribe to Pusher if at least one chat is enabled (feature flag + API level)
  const hasAnyEnabledChat = isMessagesChatEnabled || isPostsChatEnabled;
  const isPusherEnabled =
    hasAnyEnabledChat && chats.length > 0 && !!user?.id && viewer === 'participant';

  const { isConnected, expectEvent } = useLiveActivityChat({
    chats,
    enabled: isPusherEnabled,
    onMessageCreated: handleMessageCreated,
    onMessageUpdated: handleMessageUpdatedFromPusher,
    onMessageDeleted: handleMessageDeletedFromPusher,
    onReactionAdded: handleReactionAddedFromPusher,
    onReactionRemoved: handleReactionRemovedFromPusher,
    onChatStateChanged: handleChatStateChangedFromPusher,
    onUserBanned: handleUserBannedFromPusher,
    onConnectionLost: handleConnectionLost,
    onConnectionRestored: handleConnectionRestored,
    onEventTimeout: handleEventTimeout,
  });

  // Store expectEvent in the shared utils ref
  utils.expectEventRef.current = expectEvent;

  // --- Cleanup Effects ---

  const previousMessagesChatIdRef = useRef<string | undefined>(undefined);
  const previousPostsChatIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (messagesChat?.id !== previousMessagesChatIdRef.current) {
      clearPendingMessagesForMode('bidirectional');
      previousMessagesChatIdRef.current = messagesChat?.id;
    }
  }, [messagesChat?.id, clearPendingMessagesForMode]);

  useEffect(() => {
    if (postsChat?.id !== previousPostsChatIdRef.current) {
      clearPendingMessagesForMode('unidirectional');
      previousPostsChatIdRef.current = postsChat?.id;
    }
  }, [postsChat?.id, clearPendingMessagesForMode]);

  useEffect(() => {
    const CLEANUP_INTERVAL_MS = 10000;
    const cleanupInterval = setInterval(() => {
      clearStalePendingEntries();
    }, CLEANUP_INTERVAL_MS);

    return () => clearInterval(cleanupInterval);
  }, [clearStalePendingEntries]);

  useEffect(
    () => () => {
      clearAllPendingEntries();
    },
    [clearAllPendingEntries]
  );

  // --- Computed Values ---

  const notificationCount = messagesState.unreadMessagesData?.data.count || 0;
  const fetchNextPage =
    activeTab === 'posts' ? postsState.fetchNextPagePosts : messagesState.fetchNextPageMessages;
  const hasNextPage =
    activeTab === 'posts' ? postsState.hasNextPagePosts : messagesState.hasNextPageMessages;
  const isFetchingNextPage =
    activeTab === 'posts'
      ? postsState.isFetchingNextPagePosts
      : messagesState.isFetchingNextPageMessages;

  const isMessagePending = messagesState.isMessagesPending || postsState.isPostsPending;

  // --- Return (Backwards Compatible API) ---

  return {
    // Chats data
    chats,
    messagesChat,
    postsChat,
    chat,
    chatId,
    chatsData,
    isLoadingChats,
    chatsError,

    // Feature flags
    finalIsChatGloballyEnabled,
    finalIsChatWebEnabled,
    finalIsPostsWebEnabled,

    // Chat state
    isChatEnabled,
    isPostsEnabled,
    chatEnabledFromAPI,
    currentMuteNotifications,
    hasReceivedChatPusherMessage,
    hasReceivedPostPusherMessage,

    // Messages (from messagesState)
    messagesChatHistory: messagesState.messagesChatHistory,
    isLoadingMessagesChat: messagesState.isLoadingMessagesChat,
    messagesChatError: messagesState.messagesChatError,
    refetchMessagesChat: messagesState.refetchMessagesChat,

    // Posts (from postsState)
    postsChatHistory: postsState.postsChatHistory,
    isLoadingPostsChat: postsState.isLoadingPostsChat,
    postsChatError: postsState.postsChatError,
    refetchPostsChat: postsState.refetchPostsChat,
    postsChatTotalCount: postsState.postsChatTotalCount,

    // Infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Actions
    sendChatMessage: messagesState.sendChatMessage,
    sendPostMessage: postsState.sendPostMessage,
    isSendingChatMessage: isMessagePending,
    isSendingPostMessage: isMessagePending,
    pinMessage: postsState.pinMessage,
    unpinMessage: postsState.unpinMessage,
    isPinUnpinLoading: postsState.isPinUnpinLoading,

    // Reaction actions (unified handlers)
    handleAddReaction,
    handleRemoveReaction,
    isReactionPending: false,

    // Unread tracking
    unreadMessagesData: messagesState.unreadMessagesData,
    refetchUnreadMessages: messagesState.refetchUnreadMessages,
    unreadPostsData: postsState.unreadPostsData,
    refetchUnreadPosts: postsState.refetchUnreadPosts,
    notificationCount,
    markAsReadMessages: messagesState.markAsReadMessages,
    markAsReadPosts: postsState.markAsReadPosts,
    getLastSentMessagesMessageId: messagesState.getLastSentMessagesMessageId,
    getLastSentPostsMessageId: postsState.getLastSentPostsMessageId,
    hasUnreadSinceLastViewRef,

    // Connection
    isConnected,

    // Pusher event management
    expectEvent,
  };
}
